import type { NutrientKey } from './types';

export type NutrientUnit = 'kcal' | 'g' | 'mg' | 'ug';
export type NutrientKind = 'energia' | 'macro' | 'mineral' | 'vitamina' | 'grasa';

export interface NutrientMeta {
  key: NutrientKey;
  /** Nombre en es-ES tal cual se pinta. */
  label: string;
  unit: NutrientUnit;
  kind: NutrientKind;
  /** Decimales al mostrar. Las cifras grandes no llevan decimales. */
  decimals: number;
  /**
   * Los macros y la fibra se juzgan por dia; los micros, con la media de los
   * ultimos 7 dias. SPEC 6.C.
   */
  window: 'dia' | 'semana';
}

/** Orden de esta lista = orden en que se pinta en el panel del dia. */
export const NUTRIENTS: readonly NutrientMeta[] = [
  { key: 'kcal', label: 'Calorías', unit: 'kcal', kind: 'energia', decimals: 0, window: 'dia' },
  { key: 'protein', label: 'Proteína', unit: 'g', kind: 'macro', decimals: 1, window: 'dia' },
  { key: 'carbs', label: 'Carbohidratos', unit: 'g', kind: 'macro', decimals: 1, window: 'dia' },
  { key: 'sugars', label: 'Azúcares', unit: 'g', kind: 'macro', decimals: 1, window: 'dia' },
  { key: 'fat', label: 'Grasas', unit: 'g', kind: 'macro', decimals: 1, window: 'dia' },
  {
    key: 'saturatedFat',
    label: 'Grasas saturadas',
    unit: 'g',
    kind: 'macro',
    decimals: 1,
    window: 'dia',
  },
  { key: 'fiber', label: 'Fibra', unit: 'g', kind: 'macro', decimals: 1, window: 'dia' },

  { key: 'calcium', label: 'Calcio', unit: 'mg', kind: 'mineral', decimals: 0, window: 'semana' },
  { key: 'iron', label: 'Hierro', unit: 'mg', kind: 'mineral', decimals: 1, window: 'semana' },
  {
    key: 'magnesium',
    label: 'Magnesio',
    unit: 'mg',
    kind: 'mineral',
    decimals: 0,
    window: 'semana',
  },
  {
    key: 'phosphorus',
    label: 'Fósforo',
    unit: 'mg',
    kind: 'mineral',
    decimals: 0,
    window: 'semana',
  },
  {
    key: 'potassium',
    label: 'Potasio',
    unit: 'mg',
    kind: 'mineral',
    decimals: 0,
    window: 'semana',
  },
  { key: 'sodium', label: 'Sodio', unit: 'mg', kind: 'mineral', decimals: 0, window: 'dia' },
  { key: 'zinc', label: 'Zinc', unit: 'mg', kind: 'mineral', decimals: 1, window: 'semana' },
  { key: 'copper', label: 'Cobre', unit: 'mg', kind: 'mineral', decimals: 2, window: 'semana' },
  { key: 'selenium', label: 'Selenio', unit: 'ug', kind: 'mineral', decimals: 0, window: 'semana' },
  { key: 'iodine', label: 'Yodo', unit: 'ug', kind: 'mineral', decimals: 0, window: 'semana' },
  {
    key: 'manganese',
    label: 'Manganeso',
    unit: 'mg',
    kind: 'mineral',
    decimals: 2,
    window: 'semana',
  },

  {
    key: 'vitaminA',
    label: 'Vitamina A',
    unit: 'ug',
    kind: 'vitamina',
    decimals: 0,
    window: 'semana',
  },
  {
    key: 'vitaminC',
    label: 'Vitamina C',
    unit: 'mg',
    kind: 'vitamina',
    decimals: 0,
    window: 'semana',
  },
  {
    key: 'vitaminD',
    label: 'Vitamina D',
    unit: 'ug',
    kind: 'vitamina',
    decimals: 1,
    window: 'semana',
  },
  {
    key: 'vitaminE',
    label: 'Vitamina E',
    unit: 'mg',
    kind: 'vitamina',
    decimals: 1,
    window: 'semana',
  },
  {
    key: 'vitaminK',
    label: 'Vitamina K',
    unit: 'ug',
    kind: 'vitamina',
    decimals: 0,
    window: 'semana',
  },
  {
    key: 'thiamin',
    label: 'Tiamina (B1)',
    unit: 'mg',
    kind: 'vitamina',
    decimals: 2,
    window: 'semana',
  },
  {
    key: 'riboflavin',
    label: 'Riboflavina (B2)',
    unit: 'mg',
    kind: 'vitamina',
    decimals: 2,
    window: 'semana',
  },
  {
    key: 'niacin',
    label: 'Niacina (B3)',
    unit: 'mg',
    kind: 'vitamina',
    decimals: 1,
    window: 'semana',
  },
  {
    key: 'vitaminB6',
    label: 'Vitamina B6',
    unit: 'mg',
    kind: 'vitamina',
    decimals: 2,
    window: 'semana',
  },
  { key: 'folate', label: 'Folato', unit: 'ug', kind: 'vitamina', decimals: 0, window: 'semana' },
  {
    key: 'vitaminB12',
    label: 'Vitamina B12',
    unit: 'ug',
    kind: 'vitamina',
    decimals: 1,
    window: 'semana',
  },

  {
    key: 'epaDha',
    label: 'Omega-3 (EPA+DHA)',
    unit: 'mg',
    kind: 'grasa',
    decimals: 0,
    window: 'semana',
  },
] as const;

const BY_KEY = new Map<NutrientKey, NutrientMeta>(NUTRIENTS.map((n) => [n.key, n]));

export function nutrientMeta(key: NutrientKey): NutrientMeta {
  const meta = BY_KEY.get(key);
  if (!meta) throw new Error(`Nutriente desconocido: ${key}`);
  return meta;
}

export const MACRO_KEYS = ['protein', 'carbs', 'fat', 'fiber'] as const satisfies NutrientKey[];

/** Todas las claves, util para recorrer sin olvidarse de ninguna. */
export const ALL_NUTRIENT_KEYS: readonly NutrientKey[] = NUTRIENTS.map((n) => n.key);
